import { Dictionary } from 'underscore';

import { AppState } from '../app/state/AppState';

import { ExplorableItemType } from '../ts/models/ContentType';
import { IContentInfo } from '../ts/models/IContentInfo';
import { IMetricDataInfo } from './models/IMetricInfo';
import { IMetricDataPoints } from '../ts/ServerApi/types/IMetricDataList';

import { Loadable, LoadingStatus } from '../ts/models/Loadable';
import { MetricsDataState, LoadableDataPointsById } from './state/MetricsDataState';
import { buildColorAccessor, buildMeasureAccessor, DataRow, EffectiveDefinition, getConstantEffectiveDefinition } from '@tableau/metrics-engine';
import { createSelector, Selector } from 'reselect';

export const getLoadableData = (state: LoadableDataPointsById, id: string): Loadable<IMetricDataPoints> => {
  const model = state[id];

  return model || Loadable.notLoaded;
};

export const getMetricsDataState = (state: AppState): MetricsDataState => {
  return state.metricsData;
};

export const getMetricsDataPoints = (state: AppState): LoadableDataPointsById => {
  return state.metricsData.dataPointsById;
};

const effectiveDefinition = getConstantEffectiveDefinition();

const getEffectiveDefinition = (state: AppState): EffectiveDefinition => {
  // This will eventually be replaced by the definition in state
  return effectiveDefinition;
};

export const getMeasureAccessor: Selector<AppState, (point: DataRow) => number> = createSelector(
  getEffectiveDefinition,
  (definition) => buildMeasureAccessor(definition)
);

export const getColorAccessor: Selector<AppState, (point: DataRow) => string> = createSelector(
  getEffectiveDefinition,
  (definition) => buildColorAccessor(definition)
);

const nonMetricData = {};

export function selectMetricDataForMetricId(state: AppState, id: string): IMetricDataInfo | undefined {
  const loadableContent = getLoadableData(getMetricsDataPoints(state), id);
  if (loadableContent.loadingStatus === LoadingStatus.LOADED) {
    return loadableContent.content;
  }

  return undefined;
}

export function selectMetricDataForContentItem(state: AppState, content: (IContentInfo<ExplorableItemType> | Dictionary<any>)): IMetricDataInfo | undefined {
  if (!content || content.contentType !== 'metric') {
    return nonMetricData;
  }

  return selectMetricDataForMetricId(state, content.id);
}

export const notLoadedData = (state: LoadableDataPointsById, ids: ReadonlyArray<string>): ReadonlyArray<string> => {
  return ids
    .map(id => ({ id, loadable: getLoadableData(state, id)}))
    .filter(item => item.loadable.loadingStatus === LoadingStatus.NOT_LOADED)
    .map(item => item.id);
};

export const getMetricRefreshList = (state: MetricsDataState): ReadonlyArray<string> => {
  return state.metricRefreshList.reduce((metricList, id) => {
    if (getLoadableData(state.dataPointsById, id).loadingStatus !== LoadingStatus.LOADING) {
      metricList.push(id);
    }
    return metricList;
  }, []);
};